import { Check, ChevronUp, CircleHelp } from 'lucide-react';
import { type ForwardedRef, forwardRef, useState } from 'react';
import styles from './ContextMenu.module.css';
import type { DisabledInfo, MenuItemNode } from './types';

/** Глифы-акселераторы → произносимое имя (для скринридера). */
const HOTKEY_SPOKEN: Record<string, string> = { '⌫': 'Backspace' };

export type ContextMenuItemProps = {
  node: MenuItemNode;
  active: boolean;
  /** Единственный табируемый пункт панели (roving tabindex). */
  tabbable: boolean;
  onActivate: () => void;
  onHover: () => void;
};

/**
 * Строка меню (макет 66:3 — колонка иконки 18px, лейбл, хоткей/галочка справа).
 *
 * disabled рисуется, а не прячется: право есть, условие не выполнено —
 * пользователь должен видеть пункт и причину (SOLUTION §5). Полностью
 * отсутствующее право убирает пункт ещё в реестре, сюда он не доходит.
 */
export const ContextMenuItem = forwardRef(function ContextMenuItem(
  { node, active, tabbable, onActivate, onHover }: ContextMenuItemProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const Icon = node.icon;
  const disabled = node.disabled;
  /**
   * F11 (project-plan-change §D) — причина запрета, раскрываемая нажатием.
   *
   * До правки полная причина жила только в `title` и `aria-describedby`: мышь
   * получала её всплывающей подсказкой, скринридер — описанием, а палец не
   * получал НИЧЕГО. Нажатие на выключенный пункт было единственным жестом в
   * меню без какого-либо отклика — молчание, неотличимое от поломки.
   *
   * Вариант «показывать причину всегда» отвергнут осознанно: он развернул бы
   * решение F16 пака `context-menu`, которое сняло постоянную пометку именно
   * из-за ширины (подпись «Перенести в другую компанию…» занимает почти всю
   * строку 360px). Здесь строка остаётся однострочной по умолчанию и растёт
   * только у того пункта, по которому нажали, и только когда спросили.
   *
   * Узел причины ОДИН на оба канала: раскрытие снимает с него `srOnly`, а не
   * добавляет второй текст, — иначе скринридер прочитал бы причину дважды.
   */
  const [whyRevealed, setWhyRevealed] = useState(false);
  // Галочка без роли видна только зрячим. Роль выбирается по семантике набора:
  // статус/приоритет — один из многих, метки — независимые переключатели.
  const role =
    node.selection === 'radio'
      ? 'menuitemradio'
      : node.selection === 'checkbox'
        ? 'menuitemcheckbox'
        : 'menuitem';
  return (
    <button
      ref={ref}
      type="button"
      role={role}
      {...(node.selection ? { 'aria-checked': node.checked === true } : {})}
      tabIndex={tabbable ? 0 : -1}
      className={[
        styles.item,
        node.danger ? styles.itemDanger : '',
        disabled ? styles.itemDisabled : '',
        disabled && whyRevealed ? styles.itemWhyRevealed : '',
        active ? styles.itemActive : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-disabled={disabled ? true : undefined}
      {...(disabled
        ? {
            title: disabled.reason,
            'aria-describedby': `${node.id}-why`,
            // Пункт нельзя выполнить, но по нему МОЖНО нажать — и это состояние
            // раскрытия, а не действие. Без `aria-expanded` скринридер объявил бы
            // его просто недоступным, и раскрытие осталось бы секретом.
            'aria-expanded': whyRevealed,
          }
        : {})}
      onMouseEnter={onHover}
      onClick={() => {
        if (disabled) {
          setWhyRevealed((v) => !v);
          return;
        }
        onActivate();
      }}
    >
      <span
        className={styles.icon}
        aria-hidden="true"
        {...(node.iconColor ? { style: { color: node.iconColor } } : {})}
      >
        {node.dotColor ? (
          <span className={styles.dot} style={{ background: node.dotColor }} />
        ) : Icon ? (
          <Icon size={14} />
        ) : null}
      </span>
      <span className={styles.label}>{node.label}</span>
      {/*
        Пометка причины стоит сразу за подписью, до хоткея: «Вставить · буфер
        пуст · Ctrl+V» читается как объяснение, «Вставить · Ctrl+V · буфер
        пуст» — как оторванный хвост. Полная причина — в `title` и скрытом
        описании; пометка ужимается первой и не режет название действия.
      */}
      {disabled?.note && <span className={styles.note}>{disabled.note}</span>}
      {/*
        Гейт фазы D — знак того, что у пункта есть объяснение.
        Оба ревьюера сказали об этом одними словами: «у выключенных пунктов нет
        признака, что причину можно раскрыть» — механика F11 обнаруживалась
        случайным тыком. Знак стоит в правой колонке, где у остальных пунктов
        хоткей или шеврон, то есть в привычном месте «что будет по нажатию».
      */}
      {disabled && (
        <span className={styles.whyHint} aria-hidden="true">
          {/* Второй проход гейта: 12px muted-глифа на выключенной строке физически
              не видно — UI-ревьюер замерил, что это ниже минимума в 16px, а цель
              нажатия меньше 24×24. Знак поднят до 16px в области 24×24 и до
              вторичного тона; в раскрытом состоянии он МЕНЯЕТСЯ, иначе «открыто»
              и «закрыто» выглядят одинаково. */}
          {whyRevealed ? <ChevronUp size={16} /> : <CircleHelp size={16} />}
        </span>
      )}
      {/* Значение резервирует колонку шеврона: у раскрывателя она занята, и без
          резерва «Планирование ›» и «Открытый» стоят на разных вертикалях.
          Резерв — только когда значение действительно последнее в строке: перед
          хоткеем или галочкой он превратился бы в лишний зазор. */}
      {node.meta && (
        <span
          className={
            node.hotkey || node.checked ? styles.meta : `${styles.meta} ${styles.metaReserve}`
          }
        >
          {node.meta}
        </span>
      )}
      {node.hotkey && (
        // Глиф ⌫ произносится скринридером как мусор или молчит — даём словесное
        // имя. Остальные подписи уже словесные и в подмене не нуждаются.
        <span
          className={styles.hotkey}
          {...(HOTKEY_SPOKEN[node.hotkey] ? { 'aria-label': HOTKEY_SPOKEN[node.hotkey] } : {})}
        >
          {node.hotkey}
        </span>
      )}
      {disabled && (
        <span className={whyRevealed ? styles.why : styles.srOnly} id={`${node.id}-why`}>
          {disabled.reason}
        </span>
      )}
      {node.checked && (
        <span className={styles.check} aria-hidden="true">
          <Check size={14} />
        </span>
      )}
    </button>
  );
});

/** Хелпер для тестов: пункт кликабелен? */
export function itemEnabled(disabled: DisabledInfo | undefined): boolean {
  return disabled === undefined;
}
